import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserCircle, Upload, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
  { kind: 'avatar', label: 'Profile Picture', hint: 'square' },
  { kind: '16x9', label: 'Host Slide (16:9)', hint: 'trivia presentation' },
  { kind: '9x16', label: 'Story Slide (9:16)', hint: 'social story video' },
];
const FIELDS = [
  { key: 'display_name', label: 'Display Name' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'home_city', label: 'Home City' },
];

function ImageSlot({ personId, kind, label, hint, current, onChanged }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url = null;
    if (current) {
      ax.get(`${API}/hosts/${personId}/images/${kind}/raw`, { responseType: 'blob' })
        .then((r) => { url = URL.createObjectURL(r.data); setPreview(url); })
        .catch(() => setPreview(null));
    } else setPreview(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [current, personId, kind]);

  const pick = async (file) => {
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try { await ax.post(`${API}/hosts/${personId}/images/${kind}`, form); toast.success(`${label} updated`); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await ax.delete(`${API}/hosts/${personId}/images/${kind}`); toast.success(`${label} removed`); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
      <div className={`shrink-0 overflow-hidden rounded-lg bg-[#000e2a] border border-[rgba(251,221,104,0.1)] ${kind === '9x16' ? 'w-9 h-16' : kind === '16x9' ? 'w-16 h-9' : 'w-12 h-12'}`}>
        {preview && <img src={preview} alt={label} className="w-full h-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white font-medium">{label}</p>
        <p className="text-xs text-[#8892b0]">{current ? current.name : `png, jpg, gif or webp — ${hint}`}</p>
      </div>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }} />
      <Button data-testid={`upload-${kind}-btn`} onClick={() => ref.current?.click()} disabled={busy} size="sm"
        className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685] h-8 px-3 text-xs">
        <Upload className="w-3.5 h-3.5 mr-1" />{busy ? '...' : current ? 'Replace' : 'Upload'}
      </Button>
      {current && (
        <Button data-testid={`delete-${kind}-btn`} onClick={remove} variant="ghost" size="sm"
          className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10 h-8 px-2"><Trash2 className="w-4 h-4" /></Button>
      )}
    </div>
  );
}

export const MyProfileCard = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await ax.get(`${API}/me/profile`);
      setProfile(data);
      setForm(Object.fromEntries(FIELDS.map((f) => [f.key, data[f.key] || ''])));
      const imgs = await ax.get(`${API}/hosts/${data.person_id}/images`);
      setImages(imgs.data.images || []);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await ax.patch(`${API}/me/profile`, form); toast.success('Profile saved — linked programs will pick it up'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!profile) return null;
  return (
    <Card data-testid="my-profile-card" className="glass-panel border-0">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <UserCircle className="w-5 h-5 text-[#fbdd68]" />My Profile
        </CardTitle>
        <CardDescription className="text-[#8892b0]">
          Your personal details and host images. Every linked BIG Hat program syncs these automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-mono uppercase tracking-widest text-[#8892b0]">{f.label}</label>
              <Input data-testid={`profile-${f.key}`} value={form[f.key] || ''}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                className="mt-1 bg-[#000e2a] border-[rgba(251,221,104,0.2)] text-white" />
            </div>
          ))}
        </div>
        <Button data-testid="save-profile-btn" onClick={save} disabled={saving}
          className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
        <div className="space-y-2 pt-2 border-t border-[rgba(251,221,104,0.08)]">
          <p className="text-xs font-mono uppercase tracking-widest text-[#8892b0]">Host Images</p>
          {KINDS.map((k) => (
            <ImageSlot key={k.kind} personId={profile.person_id} kind={k.kind} label={k.label} hint={k.hint}
              current={images.find((i) => i.kind === k.kind)} onChanged={load} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
